/**
 * Card components — the repeating tiles of the approved mock-up: service,
 * industry, case study, insight article and lead magnet.
 *
 * Every card that navigates is an `<a>` wrapping the whole tile, so the target
 * is one tab stop with a real href (the mock-up used click handlers on divs,
 * which are neither focusable nor announced as links).
 */
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { IndustrySeed, MagnetSeed } from "@mb-group/content-seed";
import type { Article, CaseStudy, Service } from "../data/types.js";

export function serviceCard(ctx: AppContext, service: Service): HTMLElement {
	return el(
		"a",
		{ class: "mb-card mb-card--service", href: ctx.href("serviceDetail", { slug: service.slug }) },
		el("h3", { class: "mb-card__title" }, service.title),
		el("p", { class: "mb-card__text" }, service.usp),
		el("span", { class: "mb-card__more" }, ctx.copy().services.viewService),
	);
}

/** Compact variant used in the "other services in this category" strip. */
export function relatedServiceCard(ctx: AppContext, service: Service): HTMLElement {
	return el(
		"a",
		{ class: "mb-card mb-card--related", href: ctx.href("serviceDetail", { slug: service.slug }) },
		el("h3", { class: "mb-card__title" }, service.title),
		el("p", { class: "mb-card__text" }, service.usp),
	);
}

export function industryCard(industry: IndustrySeed, variant: "strip" | "full"): HTMLElement {
	const card = el(
		"article",
		{ class: `mb-card mb-card--industry mb-card--industry-${variant}` },
		el("h3", { class: "mb-card__title" }, industry.name),
		el("p", { class: "mb-card__text" }, industry.desc),
	);
	if (variant === "full") card.appendChild(el("p", { class: "mb-card__tag" }, industry.tag));
	return card;
}

export function caseCard(ctx: AppContext, study: CaseStudy): HTMLElement {
	// Defensive: a thinner selection set (or a case study with no metrics) must
	// never crash the whole route — the `metric ?` guard below handles absence.
	// This is the failure mode that took the home page down once already; see
	// `fix(frontend-v2): stop the home page 500 — select case-study metrics`.
	const metric = study.metrics?.[0];
	const media = study.heroImage
		? el("img", {
				class: "mb-card__media",
				src: study.heroImage.url,
				alt: study.heroImage.alt ?? "",
				loading: "lazy",
				decoding: "async",
			})
		: null;

	const body = el("div", { class: "mb-card__body" });
	// The eyebrow prefers the service category, but `CaseStudyCardFields` does not
	// select `services { category { title } }` — so on a LIVE response that is
	// absent and only the DEMO projection has it. Fall back to the company name,
	// which the fragment does select, rather than rendering an empty eyebrow.
	// `services?.[0]`, not `services[0]`: the key is ABSENT on a LIVE response, so
	// indexing it threw a TypeError and took the whole home route down. The comment
	// above had the diagnosis right while this line contradicted it.
	// The design's eyebrow is the case study's own short stream label. It is only
	// present on the DEMO projection (and on LIVE once the gateway selects it), so
	// the service category and company name stay as fallbacks — this chain is what
	// keeps an incomplete response from taking the home page down.
	const tag = study.tag ?? study.services?.[0]?.category?.title ?? study.company?.name;
	if (tag) body.appendChild(el("p", { class: "mb-eyebrow" }, tag));
	body.appendChild(el("h3", { class: "mb-card__title" }, study.title));
	body.appendChild(el("p", { class: "mb-card__text" }, study.outcome));
	if (metric) {
		body.appendChild(
			el(
				"div",
				{ class: "mb-card__metric" },
				el("span", { class: "mb-card__metricvalue" }, metric.value),
				el("span", { class: "mb-card__metriclabel" }, metric.label),
			),
		);
	}

	return el(
		"a",
		{ class: "mb-card mb-card--case", href: ctx.href("caseStudyDetail", { slug: study.slug }) },
		media,
		body,
	);
}

export function insightCard(ctx: AppContext, article: Article): HTMLElement {
	const tag = article.tags[0];
	return el(
		"a",
		{ class: "mb-card mb-card--insight", href: ctx.href("article", { slug: article.slug }) },
		tag ? el("p", { class: "mb-eyebrow" }, tag) : null,
		el("h3", { class: "mb-card__title" }, article.title),
		el("p", { class: "mb-card__text" }, article.excerpt),
		el("span", { class: "mb-card__more" }, ctx.copy().knowledge.read),
	);
}

/**
 * Lead-magnet tile. Opens the gate modal rather than navigating, so it is a
 * button — the download is gated behind an e-mail, which is an action, not a page.
 */
export function magnetCard(ctx: AppContext, magnet: MagnetSeed, index: number): HTMLElement {
	return el(
		"button",
		{
			class: "mb-card mb-card--magnet",
			type: "button",
			onClick: () => ctx.openGate(index),
		},
		el("span", { class: "mb-pill mb-pill--accent" }, magnet.kind),
		el("span", { class: "mb-card__title" }, magnet.title),
		el("span", { class: "mb-card__text" }, magnet.desc),
		el(
			"span",
			{ class: "mb-card__foot" },
			el("span", { class: "mb-card__meta" }, magnet.meta),
			el("span", { class: "mb-card__more" }, `${magnet.cta} →`),
		),
	);
}

/** A category block on the services catalogue page. */
export function categoryBlock(
	ctx: AppContext,
	category: {
		slug: string;
		title: string;
		summary: string;
		icon?: string | null;
		services: Service[];
	},
): HTMLElement {
	const grid = el("div", { class: "mb-grid mb-grid--3" });
	for (const service of category.services) grid.appendChild(serviceCard(ctx, service));

	return el(
		"section",
		{ class: "mb-catblock", id: `kategoria-${category.slug}` },
		el(
			"div",
			{ class: "mb-catblock__head" },
			el("span", { class: "mb-catblock__num" }, category.icon ?? ""),
			el("h2", { class: "mb-catblock__title" }, category.title),
		),
		el("p", { class: "mb-catblock__lead" }, category.summary),
		grid,
	);
}
